import React, { useEffect, useState } from 'react';
import { GoogleMap, GoogleMapProps, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { hk_lat_lng } from '@/constants/map';
import { LatLng } from '@/models/core.model';
import { getBoundingBoxCenter } from '@/utils/map';
import { toastErrorMessage } from '@/utils/toasterMessage';

type MapToSelectPathProps = {
  value?: LatLng[];
  onChange?: (path: LatLng[]) => void;
  defaultCenter?: LatLng;
  height?: string;
  disabled?: boolean;
  stepsLimit?: number; 
} & Omit<GoogleMapProps, "data" | "onSearch">;

const MapToSelectPath: React.FC<MapToSelectPathProps> = ({
  value: path,
  onChange: onPathChange,
  defaultCenter = hk_lat_lng,
  height = '350px',
  disabled = false,
  stepsLimit = 20,
  ...rest 
}) => {
  const [steps, setSteps] = useState<LatLng[]>([]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY!,
  });

  useEffect(() => {
    computePath();
  }, [steps]);
  
  const computePath = () => {
    if(steps.length > 1){
      const directionsService = new google.maps.DirectionsService();
      directionsService.route(
        {
          origin: steps[0],
          destination: steps[steps.length - 1],
          waypoints: steps.slice(1, steps.length).map(p => ({location: p, stopover: false})),
          travelMode: google.maps.TravelMode.WALKING,
          unitSystem: google.maps.UnitSystem.METRIC
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK) {
            if(result?.routes){
              const path = getPathLatLngList(result);
              onPathChange && onPathChange(path);
            }
          } else {
            toastErrorMessage('Error fetching direction from Google');
            console.error('Error fetching directions', result);
          }
        }
      );
    }
  };

  const getPathLatLngList = (directionsResult: google.maps.DirectionsResult) => {
    const route = directionsResult.routes[0];
    const detailedLatLngList: ({lat:number, lng:number})[] = [];

    route.legs.forEach(leg => {
      leg.steps.forEach(step => {
        step.path.forEach(latlng => {
          detailedLatLngList.push({ lat: latlng.lat(), lng: latlng.lng() });
        });
      });
    });

    return detailedLatLngList;
  };

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if(!e.latLng || disabled){return;}
    if(steps.length < stepsLimit){
      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      setSteps(pre => [...pre, position]);
    }else{
      toastErrorMessage('Step limit is reached');
    }
  };

  const handleMarkerDrag = (key: number, e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      setSteps(prev => {
        const updated = [...prev]; // Copy the array
        updated[key] = position; // Modify the specific index
        return updated;
      });
    };
  };

  const handleMarkerDbClick = (key: number) => {
    setSteps(prev => prev.filter((_, index) => index !== key));
  };

  const polylineOptions = {
    strokeColor: '#FF6F61', // coral-red color
    strokeOpacity: 0.8,
    strokeWeight: 5,
    geodesic: true,
  };  

  const getMapCenter = () : LatLng => {
    if(steps && steps.length > 0) return getBoundingBoxCenter(steps)!;
    if(path && path.length > 0) return getBoundingBoxCenter(path)!;
    return defaultCenter;
  };

  if (!isLoaded) return <h6>Loading Map...</h6>;

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: height, borderRadius: 10 }}
      center={getMapCenter()}
      zoom={15}
      onClick={handleMapClick}
      {...rest}
    >
      {steps.map( (point, index) => 
        <React.Fragment key={index}>
          <Marker
            title='Double Click To Remove'
            key={index} 
            position={point} 
            label={(index + 1).toString()} 
            onDragEnd={e => handleMarkerDrag(index, e)}
            onDblClick={e => handleMarkerDbClick(index)}
            draggable
          />
        </React.Fragment>
      )}
      {path && path.length > 1 && <Polyline
        path={path}
        options={polylineOptions}
      />}                    
    </GoogleMap> 
  );
};

export default MapToSelectPath;
